import Checkbox from "components/shared/Checkbox"
import { Button } from "components/shared/panda/Button"
import { Input as BaseInput } from "components/shared/panda/Input"
import { PhoneInput } from "components/shared/panda/PhoneInput"
import { getPath, getUtmFields } from "lib/helpers"
import dynamic from "next/dynamic"
import Link from "next/link"
import { useRouter } from "next/router"
import { Trans, useTranslation } from "next-i18next"
import { useCallback, useEffect, useRef, useState } from "react"
import { VStack } from "styled-system/jsx"

import { ContactFormProps } from "../ContactForm"
import FormWrapper, { FormResponse } from "../FormWrapper"
import CustomFields from "./CustomFields"

const MarketingFieldsInput = dynamic(
  () => import("components/shared/inputs/MarketingFieldsInput"),
  { ssr: false }
)

type Props = ContactFormProps

function GenericForm({ customForm }: Props) {
  const [data, setData] = useState<FormResponse>()

  const { t } = useTranslation()
  const { query, locale, push } = useRouter()

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault()

      // @ts-expect-error
      if (event._manualDispatch) return
      else event.stopImmediatePropagation()

      setData(null)

      const customFields: { field: string; value: string }[] = customForm
        ? [
            { field: "form_id", value: customForm.formId },
            ...getUtmFields(query),
            ...(customForm.fields || [])?.reduce((acc, curr) => {
              acc.push({
                field: curr.hubspotId,
                value: event.target[curr.hubspotId].value,
              })
              return acc
            }, []),
          ]
        : [{ field: "form_id", value: "contact-form" }]

      const element = event.target as HTMLFormElement
      const data = {
        first_name: element.firstName.value,
        last_name: element.lastName.value,
        email: element.email.value,
        phone: element.phone.value,
        terms: element.terms.checked,
        tags: ["inbound", ...(customForm?.tags || [])],
        custom_fields: customFields,
      }

      const endpoint = "/api/create-lead"

      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }

      const response = await fetch(endpoint, options)

      const result = await response.json()

      setData(result)

      if (!result.error) {
        const newEvent = new Event(event.type, event)
        newEvent._manualDispatch = true
        element.dispatchEvent(newEvent)
        setTimeout(() => {
          element?.reset()
        }, 500)

        if (customForm.thankYouPage) push(customForm.thankYouPage as string)
      }
    },
    [customForm, push]
  )

  const formRef = useRef<HTMLFormElement>()
  useEffect(() => {
    if (!formRef.current) return

    Object.entries(query).forEach(([key, value]) => {
      const field = formRef.current.elements.namedItem(key)
      if (field && value && "value" in field && typeof value === "string")
        field.value = value
    })
  }, [query])

  useEffect(() => {
    formRef.current?.addEventListener("submit", handleSubmit, true)
    return () =>
      formRef.current?.removeEventListener("submit", handleSubmit, true)
  }, [handleSubmit])

  return (
    <FormWrapper
      ref={formRef}
      id={customForm?.formId || "contact-form"}
      method="POST"
      action="/api/create-lead"
      onSubmit={e => e.preventDefault()}
      data={data}
      data-type="embeddedForm-generic">
      <VStack alignItems="start" gap={6}>
        <Input name="firstName" label={t("firstname")} />
        <Input name="lastName" label={t("lastname")} />
        <Input name="email" label={t("email")} />
        <PhoneInput name="phone" placeholder={t("phone")} />

        <CustomFields fields={customForm?.fields} />
      </VStack>

      <VStack alignItems="stretch" gap={4}>
        <Checkbox
          label={
            <Trans
              i18nKey="termsLabel"
              components={{
                terms: <Link href={getPath("termsConditions", locale)} />,
                privacy: <Link href={getPath("privacyPolicy", locale)} />,
              }}
            />
          }
          name="terms"
        />
        <MarketingFieldsInput />
        <Button variant="filled" fillColor="primary" color="white">
          {customForm?.submitLabel || t("submit")}
        </Button>
      </VStack>
    </FormWrapper>
  )
}

export const Input = ({ label, name, ...rest }) => {
  return (
    <VStack width="100%" gap={1} alignItems="start" color="#000">
      <BaseInput
        name={name}
        placeholder={label}
        {...rest}
        css={{ p: "0 16px" }}
      />
    </VStack>
  )
}

export default GenericForm
export type { Props as GenericFormProps }
